import React, { useMemo } from 'react'
import { MenuNewWindow, ThingMapPin } from '@fs/zion-icon'
import { css } from '@linaria/core'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { ComboMenu, Button } from '@fs/zion-ui'
import { boolAttr } from '@fs/zion-frontend-friends'
import StaticMap from './StaticMap'

const stdContainerCss = css`
  /* style overrides */
  max-width: 600px;
  min-width: 80px;

  & > * {
    width: 100%;
  }
  &[tighten] div[role='button'] {
    padding-top: 4px;
    padding-bottom: 4px;
    min-height: unset;
  }
  & div[side='left'][is-icon] {
    margin-left: 0;
  }
`

const alignButtonCss = css`
  padding-top: 12px;
`
export default function PlaceSelect({
  standard,
  externalLinkType,
  mapKey,
  suggestions,
  onChange,
  showDenseUI,
  downshiftProps,
}) {
  const { t } = useTranslation()
  // Add secondary text (place type + year range) to all suggestions
  const remappedSuggestions = useMemo(
    () =>
      suggestions?.map((value) => ({
        ...value,
        secondaryText: value.standard
          ? `${value.standard?.placeType ? value.standard?.placeType : ''}${
              value.standard?.yearRange ? value.standard?.yearRange : ''
            }`
          : '',
      })) || [],
    [suggestions]
  )
  if (externalLinkType === 'map' && !mapKey) {
    throw new Error('You must supply a mapKey when specifying externalLinkType of "map"')
  }
  const showPlacesMapLink = externalLinkType === 'link'
  const showStaticMap = externalLinkType === 'map' && standard && standard?.isStandard
  const wrappingStyle = showPlacesMapLink ? { display: 'flex', justifyContent: 'space-between' } : {}

  // Show dropdown when suggestions are available (includes "None of the above")
  const shouldShowComboMenu = remappedSuggestions && remappedSuggestions.length > 0

  return standard ? (
    <div className={stdContainerCss} tighten={boolAttr(showDenseUI)}>
      <div style={wrappingStyle}>
        {shouldShowComboMenu && (
          <ComboMenu
            fullWidth
            Icon={ThingMapPin}
            options={remappedSuggestions}
            selectedOption={standard}
            setSelectedOption={onChange}
            downshiftProps={downshiftProps}
          />
        )}
        {showPlacesMapLink && (
          <div className={alignButtonCss}>
            <Button
              Icon={MenuNewWindow}
              external
              target="_blank"
              to={`https://www.familysearch.org/research/places/?focusedId=${standard?.place?.id || ''}`}
            >
              {t('place-suggest.map.label', 'Map')}
            </Button>
          </div>
        )}
      </div>
      {showStaticMap && <StaticMap standard={standard} mapKey={mapKey} />}
    </div>
  ) : null
}

PlaceSelect.propTypes = {
  /** The currently selected place standard */
  standard: PropTypes.shape({
    /** ListItem props are available here as well. */
    value: PropTypes.any,
  }),

  /** The type of Map to display */
  externalLinkType: PropTypes.oneOf(['none', 'map', 'link']),

  /** Your apps google Map developer key (required for externalLinkType = 'map')  */
  mapKey: PropTypes.string,

  /** List of suggestions the user may select a standard option from. */
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      /** ListItem props are available here as well. */
      value: PropTypes.any,
    })
  ),

  /** An event handler called whenever the user selects a standard */
  onChange: PropTypes.func,
}
